import { crewClaimResolutionMethodEnumName } from 'corso-types';
import { useId, useMemo } from 'react';
import ProductImage from '~/components/ProductImage';
import { ResolutionMethod, RollupStatusCode } from '~/utils/constants';
import { formatProductOptions, useCurrencyFormatter } from '~/utils/formatters';
import Chip from './Chip';

type Props = {
  price: number;
  quantity: number;
  status?: string;
  statusDetail?: string;
  name?: string | null;
  imgUrl?: string | null;
  className?: string;
  rollupStatus?: `${RollupStatusCode}`;
  requestedResolution: `${ResolutionMethod}`;
  claimLineItemNoteToCustomer?: string;
  productOptions?: { name: string; value: string }[];
  fees?: { displayName: string; amount: number }[];
};

export default function ClaimLineItem({
  name,
  price,
  imgUrl,
  quantity,
  status,
  statusDetail,
  className,
  requestedResolution,
  rollupStatus,
  claimLineItemNoteToCustomer,
  productOptions = [],
  fees = [],
}: Props) {
  const labelId = useId();
  const currencyFormatter = useCurrencyFormatter();

  const description = useMemo(
    () => formatProductOptions(productOptions),
    [productOptions],
  );

  const showPrice = price > 0;

  return (
    <article className={className} aria-labelledby={labelId}>
      <div className="flex gap-4">
        <ProductImage
          expandable
          variant="small"
          alt={name ?? null}
          src={imgUrl}
          quantity={quantity}
        />
        <div className="flex w-full flex-col gap-1 label-lg text-on-surface-variant">
          <div>
            <div className="flex w-full justify-between text-on-surface">
              <span id={labelId}>{name}</span>

              {showPrice && (
                <span
                  className={`${status === 'Denied' ? 'line-through' : ''}`}
                >
                  {currencyFormatter(price)}
                </span>
              )}
            </div>
            <span>{description}</span>
          </div>
          <div className="flex gap-1">
            {requestedResolution !== 'Warranty_Review' && (
              <Chip>
                Request:{' '}
                {crewClaimResolutionMethodEnumName[requestedResolution]}
              </Chip>
            )}

            {status && rollupStatus === 'Completed' && (
              <Chip>Status: {status}</Chip>
            )}
            {statusDetail && <Chip>{statusDetail}</Chip>}

            {fees.length > 0 &&
              fees.map((fee) => (
                <Chip key={fee.displayName}>
                  {fee.displayName}: -{currencyFormatter(fee.amount)}
                </Chip>
              ))}
          </div>

          {claimLineItemNoteToCustomer && (
            <p className="my-3 body-sm">
              <span className="label-md">Note: </span>
              <span>{claimLineItemNoteToCustomer}</span>
            </p>
          )}
        </div>
      </div>
    </article>
  );
}
